'use client';

/**
 * ProductListingQuickAddGiftWidget
 *
 * Headless bottom sheet component for quick-adding products with gifts from PLP.
 * Uses progressive data loading: gift data is fetched ONLY when user clicks "Add to Cart".
 *
 * Architecture:
 * 1. Renders null (headless), wrapped with withAppBottomSheet HOC
 * 2. Exposes openBottomSheet() via ref to parent
 * 3. On open: shows skeleton → fetches gift data → initializes session → renders gift selection UI
 * 4. On confirm: attaches gifts to cart item reference, calls onConfirm, destroys session
 * 5. On close/cancel: destroys session, closes bottom sheet
 */
import React, {
    useCallback,
    useImperativeHandle,
    useMemo,
    useEffect,
    useRef,
    forwardRef,
    type ForwardedRef,
    type ForwardRefRenderFunction,
} from 'react';
import {
    withAppBottomSheet,
    GiftContentSkeleton,
    type AppBottomSheetHeaderProps,
    type AppBottomSheetFooterProps,
} from '@kidslabs/mario-nextjs-react-ui-core';
import type {
    ProductListingQuickAddGiftWidgetProps,
    ProductListingQuickAddGiftWidgetRef,
} from './product-listing-quick-add-gift-widget.types';
import { GiftSelectorBottomSheetFooter } from '../gift-selector-bottom-sheet-footer/gift-selector-bottom-sheet-footer';
import { PlpGiftBottomSheetContent } from './plp-gift-bottom-sheet-content';
import { useProductGiftLazyFetch } from '../../hooks/use-product-gift-lazy-fetch.hook';
import { useGiftSelection } from '../../hooks/use-gift-selection.hook';
import { useProductGiftHook } from '../../hooks/product-gift.hook';
import { giftSelectionStoreAPI } from '../../stores/simple-gift-selection-store.public-api';
import cssStyles from './product-listing-quick-add-gift-widget.module.css';

// Memoized footer component
const MemoizedFooter = React.memo<{
    onBottomSheetClose: () => void;
    onConfirm: () => void;
}>(({ onBottomSheetClose, onConfirm }) => {
    return <GiftSelectorBottomSheetFooter onBottomSheetClose={onBottomSheetClose} onConfirm={onConfirm} />;
});

MemoizedFooter.displayName = 'MemoizedPlpFooter';

const ProductListingQuickAddGiftWidgetInner: ForwardRefRenderFunction<
    ProductListingQuickAddGiftWidgetRef,
    ProductListingQuickAddGiftWidgetProps
> = (props, ref: ForwardedRef<ProductListingQuickAddGiftWidgetRef>) => {
    const { bottomSheet, product, onConfirm, onClose } = props;

    const { fetchGiftData } = useProductGiftLazyFetch();
    const giftHook = useGiftSelection();
    const { addGiftToCartItemReferenceLogic } = useProductGiftHook({
        product: product as { sku?: string | null; id?: number | null; name?: string | null },
        fetchGiftsFromCache: true,
    });

    // Local state
    const sessionIdRef = useRef<string>('');
    // Ref to track latest quantity from the bottom sheet content
    const quantityRef = useRef<number>(1);

    // Cleanup on unmount: destroy session if still active
    useEffect(() => {
        return () => {
            if (sessionIdRef.current && giftSelectionStoreAPI.computed.sessionExists(sessionIdRef.current)) {
                giftHook.destroySession(sessionIdRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Quantity change callback from PlpGiftBottomSheetContent
    const handleQuantityChange = useCallback((newQuantity: number) => {
        quantityRef.current = newQuantity;
    }, []);

    // Close handler
    const handleBottomSheetClose = useCallback(() => {
        // Destroy transient session
        if (sessionIdRef.current && giftSelectionStoreAPI.computed.sessionExists(sessionIdRef.current)) {
            giftHook.destroySession(sessionIdRef.current);
            sessionIdRef.current = '';
        }
        bottomSheet?.close();
        onClose?.();
    }, [bottomSheet, giftHook, onClose]);

    // Confirm handler
    const handleConfirm = useCallback(async () => {
        const sessionId = sessionIdRef.current;
        if (!sessionId) {
            bottomSheet?.close();
            return;
        }

        // Get selected gifts from session
        const selectedGifts = giftSelectionStoreAPI.state.getSelectedGifts(sessionId);

        // Attach gifts to cart item reference logics
        const sessionObservable = giftSelectionStoreAPI.state.getSessionObservable(sessionId);
        if (sessionObservable) {
            addGiftToCartItemReferenceLogic({
                selectedGifts$: sessionObservable.selectedGifts,
                sessionId,
            });
        }

        // Call onConfirm with all data needed for add-to-cart
        await onConfirm({
            product,
            quantity: quantityRef.current,
            selectedGifts: [...selectedGifts],
            sessionId,
        });

        // Destroy transient session
        giftHook.destroySession(sessionId);
        sessionIdRef.current = '';
        bottomSheet?.close();
    }, [addGiftToCartItemReferenceLogic, bottomSheet, giftHook, onConfirm, product]);

    // Header config
    const headerProps = useMemo<AppBottomSheetHeaderProps>(
        () => ({
            title: 'Chọn quà tặng',
            titleProps: { variant: 'h2', weight: 'bold', align: 'center' },
        }),
        []
    );

    // Footer element
    const footerElement = useMemo<AppBottomSheetFooterProps>(
        () => ({
            component: <MemoizedFooter onBottomSheetClose={handleBottomSheetClose} onConfirm={handleConfirm} />,
        }),
        [handleBottomSheetClose, handleConfirm]
    );

    // Open bottom sheet
    const openBottomSheet = useCallback(async () => {
        const sku = product.sku;
        if (!sku || !bottomSheet) return;

        // Destroy previous session to ensure a fresh start for each add-to-cart click
        if (sessionIdRef.current && giftSelectionStoreAPI.computed.sessionExists(sessionIdRef.current)) {
            giftHook.destroySession(sessionIdRef.current);
            sessionIdRef.current = '';
        }

        // Reset quantity
        quantityRef.current = 1;

        // 1. Show loading state immediately
        const loadingContent = (
            <div className={cssStyles.loadingContainer}>
                <GiftContentSkeleton />
            </div>
        );

        bottomSheet.inject(loadingContent, headerProps, undefined, footerElement);
        bottomSheet.show();

        // 2. Fetch gift data (uses cache if available)
        const giftsData = await fetchGiftData(sku);

        if (giftsData.length === 0) {
            // No gifts available — close bottom sheet and add to cart directly
            bottomSheet.close();
            await onConfirm({
                product,
                quantity: 1,
                selectedGifts: [],
                sessionId: '',
            });
            return;
        }

        // 3. Initialize transient session
        // IMPORTANT: Use the session ID returned by initializeSession, NOT a custom-generated one.
        // The store generates its own session ID via generateSessionKey(entityType, entityId).
        const actualSessionId = giftHook.initializeSession({
            entityId: `${product.id}`,
            entityType: 'product',
            availableGifts: giftsData,
            productQuantity: 1,
        });
        sessionIdRef.current = actualSessionId;

        // Set as active session
        giftHook.setActiveSession(actualSessionId, `${product.id}`, 'product');

        // 4. Re-inject with round 1 gift selection content (same as PDP)
        const fullContent = (
            <PlpGiftBottomSheetContent
                sessionId={actualSessionId}
                sortedGiftsDataList={giftsData}
                availableGifts={giftsData}
                onQuantityChange={handleQuantityChange}
            />
        );

        bottomSheet.inject(fullContent, headerProps, undefined, footerElement);
    }, [product, bottomSheet, headerProps, footerElement, fetchGiftData, giftHook, handleQuantityChange, onConfirm]);

    // Expose API via ref
    useImperativeHandle(
        ref,
        () => ({
            openBottomSheet,
        }),
        [openBottomSheet]
    );

    // Headless component
    return null;
};

ProductListingQuickAddGiftWidgetInner.displayName = 'ProductListingQuickAddGiftWidgetInner';

// Export component with forwardRef and HOC
export const ProductListingQuickAddGiftWidget = withAppBottomSheet(forwardRef(ProductListingQuickAddGiftWidgetInner));
